import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

const LINKS = [
  { label: "Services", id: "services" },
  { label: "Philosophy", id: "philosophy" },
  { label: "About", id: "about" },
  { label: "Journal", href: "/journal" },
  { label: "Contact", id: "contact" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const goSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(
        () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }),
        200
      );
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 left-0 right-0 z-50 border-b border-espresso/10 bg-ivory/80 backdrop-blur-md"
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <Link
          to="/"
          data-testid="nav-logo"
          className="font-roman text-[1.55rem] tracking-[0.18em] text-espresso"
        >
          <span className="mr-3 text-sienna">☾</span>ɪɴʙᴏʀɴ<span className="text-sienna">ʙɪʀᴛʜ</span><span className="ml-3 text-sienna">☽</span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links">
          {LINKS.map((l) =>
            l.href ? (
              <Link
                key={l.label}
                to={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-sm font-medium uppercase tracking-[0.15em] text-cocoa transition-colors hover:text-sienna"
              >
                {l.label}
              </Link>
            ) : (
              <button
                key={l.label}
                onClick={() => goSection(l.id)}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-sm font-medium uppercase tracking-[0.15em] text-cocoa transition-colors hover:text-sienna"
              >
                {l.label}
              </button>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/book"
            data-testid="nav-book-button"
            className="hidden rounded-full bg-sienna px-6 py-2.5 text-sm font-medium uppercase tracking-[0.12em] text-ivory transition-colors hover:bg-sienna-dark lg:inline-block"
          >
            Book a visit
          </Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="nav-mobile-menu-button"
                className="rounded-full border border-espresso/20 p-2.5 lg:hidden"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" strokeWidth={1.5} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-ivory">
              <div className="mt-10 flex flex-col gap-6">
                {LINKS.map((l) =>
                  l.href ? (
                    <Link
                      key={l.label}
                      to={l.href}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                      className="font-serif text-3xl text-espresso"
                    >
                      {l.label}
                    </Link>
                  ) : (
                    <button
                      key={l.label}
                      onClick={() => goSection(l.id)}
                      data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                      className="text-left font-serif text-3xl text-espresso"
                    >
                      {l.label}
                    </button>
                  )
                )}
                <Link
                  to="/book"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-book-button"
                  className="mt-4 rounded-full bg-sienna px-6 py-3 text-center text-sm font-medium uppercase tracking-[0.12em] text-ivory"
                >
                  Book a visit
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </motion.header>
  );
}
